import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState, type ReactElement } from 'react';
import { useNavigate } from 'react-router-dom';

import { PageHeader } from '@/components/admin/AdminShell';
import { type Column, ListTable, type ListState } from '@/components/admin/ListTable';
import { Notice } from '@/components/admin/Notice';
import { fetchInvitations } from '@/features/invitations/api';
import { PendingInvitations } from '@/features/invitations/PendingInvitations';
import { PhotoPreview } from '@/features/members/MemberPhotoField';
import { fetchStaff, setStaffActive, type Staff } from '@/features/staff/api';
import { ApiError } from '@/lib/api/http';
import { formatDateTime } from '@/lib/datetime';

export type StaffRole = 'director' | 'success';

/** 理事・講師／サクセス担当それぞれ専用のメニュー・画面にする（受講生と同じ考え方 / D-143） */
export const STAFF_ROLE_META: Record<StaffRole, { label: string; path: string }> = {
  director: { label: '理事・講師', path: '/directors' },
  success: { label: 'サクセス担当', path: '/success-staff' },
};

/**
 * 理事・講師／サクセス担当それぞれの一覧（事務局のみ / D-075・D-143）。
 * かつては1画面をタブで切り替えていたが、受講生（会員管理）と同じように
 * 区分ごとに独立したメニュー・画面にした。受講生は「会員管理」、事務局は
 * 「事務局アカウント」が別にあるため、ここでは扱わない。
 */
export function StaffPage({ role }: { role: StaffRole }): ReactElement {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const meta = STAFF_ROLE_META[role];
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  const query = useQuery({
    queryKey: ['staff', role],
    queryFn: () => fetchStaff({ role }),
  });
  // 招待中（まだ受諾されていない）の担当者（D-140）
  const invitationsQuery = useQuery({
    queryKey: ['invitations', 'staff-pending', role],
    queryFn: () => fetchInvitations(1, 100),
  });
  const pendingInvitations = (invitationsQuery.data?.items ?? []).filter(
    (i) => i.status === 'pending' && i.role === role,
  );

  const filteredRows = useMemo(() => {
    const rows = query.data ?? [];
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (s) =>
        (s.display_name ?? '').toLowerCase().includes(q) || s.email.toLowerCase().includes(q),
    );
  }, [query.data, search]);

  const activeMutation = useMutation({
    mutationFn: (input: { userId: string; isActive: boolean }) =>
      setStaffActive(input.userId, input.isActive),
    onSuccess: async () => {
      setError(null);
      await queryClient.invalidateQueries({ queryKey: ['staff'] });
    },
    onError: (err: unknown) =>
      setError(err instanceof ApiError ? err.message : '変更に失敗しました。'),
  });

  let state: ListState = 'ready';
  if (query.isPending) state = 'loading';
  else if (query.isError) {
    state = query.error instanceof ApiError && query.error.status === 403 ? 'forbidden' : 'error';
  }

  const columns: Column<Staff>[] = [
    {
      key: 'display_name',
      header: '氏名',
      primary: true,
      // 会員一覧と同じく、氏名の横に顔写真を出す（当日その場で本人を確認できる）
      render: (s) => (
        <span className="flex items-center gap-2">
          <PhotoPreview photoUrl={s.photo_url} name={s.display_name ?? s.email} size={32} />
          {s.display_name ?? '（未設定）'}
        </span>
      ),
    },
    { key: 'email', header: 'メールアドレス', render: (s) => s.email },
    // 担当する面談回数（サクセス面談／1on1）。会員台帳への導線はやめた（D-301）
    {
      key: 'meeting_count',
      header: role === 'success' ? '実施サクセス面談回数' : '実施1on1回数',
      className: 'whitespace-nowrap',
      render: (s) => (
        <>
          {s.meeting_held_count} 回
          {s.meeting_planned_count > 0 && (
            <span className="ml-1 text-wptext-muted">
              （予定 {s.meeting_planned_count} 回）
            </span>
          )}
        </>
      ),
    },
    { key: 'is_active', header: '状態', render: (s) => (s.is_active ? '有効' : '停止中') },
    {
      key: 'last_login_at',
      header: '最終ログイン',
      render: (s) => (s.last_login_at ? formatDateTime(s.last_login_at) : '—'),
    },
  ];

  return (
    <>
      <PageHeader
        title={meta.label}
        description={`${meta.label}の一覧です。右上の「招待する」から招待メールを送ります（本人が受諾するとこの一覧に出てきます）。退任した方は「停止」にするとログインできなくなります。`}
        actions={
          <button
            type="button"
            onClick={() => navigate(`${meta.path}/new`)}
            className="rounded-sm border border-transparent bg-wpaccent px-3.5 py-2 text-white hover:bg-wpaccent-hover"
          >
            招待する
          </button>
        }
      />

      {error && (
        <Notice kind="error" dismissible={false}>
          {error}
        </Notice>
      )}

      <PendingInvitations
        invitations={pendingInvitations}
        invalidateKeys={[['invitations', 'staff-pending', role]]}
      />

      <ListTable<Staff>
        caption={`${meta.label}の一覧`}
        rows={filteredRows}
        columns={columns}
        rowKey={(s) => s.user_id}
        rowLabel={(s) => s.display_name ?? s.email}
        state={state}
        errorMessage="一覧の取得に失敗しました。"
        onRetry={() => void query.refetch()}
        searchValue={search}
        onSearch={(value) => setSearch(value)}
        searchPlaceholder="氏名・メールで検索"
        rowActions={[
          {
            label: 'プロフィールを編集',
            onSelect: (s) => navigate(`${meta.path}/${s.user_id}/edit`),
          },
          {
            label: '有効・停止を切り替える',
            onSelect: (s) =>
              activeMutation.mutate({ userId: s.user_id, isActive: !s.is_active }),
          },
          {
            // 担当イベント一覧（種別分け・これから／終了。会員の参加イベントと揃える / D-303）
            label: '担当イベントを見る',
            onSelect: (s) => navigate(`${meta.path}/${s.user_id}/meetings`),
          },
        ]}
        emptyTitle={`登録されている${meta.label}はいません`}
        emptyDescription={`「招待する」から${meta.label}を招待してください。`}
      />
    </>
  );
}
